/**
 * V7.2.C Personnel bundle (2026-06-02) — eager-loaded catalog of every
 * NPC across seed/canonical/personnel/.
 *
 * 228KB of JSON imported at build time via the `@seed` vite alias. The
 * catalog is a Map<id, NpcSheetData> the frontend useNpcLookup consults
 * after scenario.key_npcs (which take precedence so scenario-specific
 * overrides win).
 *
 * Per-source field shapes (verified 2026-06-02):
 *   allies_enemies.json:
 *     .allies[]                 — full dossier; allegiance='ally'
 *     .enemies[]                — full dossier; allegiance='enemy'
 *     .allies_cross_referenced[] — shorter; allegiance='ally'
 *     Each entry has: id, name, role, allegiance, page, background_summary,
 *                     stats?, skills?, physical?, combat?
 *   mi6_personnel.json + q_branch.json:
 *     .dossiers[]               — code_name + biographical.{summary,background}
 *   bond_npc.json:               single Bond record at top-level
 *   pc_pregens.json:             .pregens[] — player characters
 *   tarot.json:                  .dossiers[] — TAROT terrorist organization
 *                                (tagged with allegiance='tarot' so the
 *                                ScenarioRunner isTarot heuristic +
 *                                DossierExporter redaction work for the
 *                                9 TAROT lieutenants + Skorpios).
 */
import { NpcSheetData } from '../types/scenario';
import alliesEnemies from '@seed/canonical/personnel/allies_enemies.json';
import mi6Personnel from '@seed/canonical/personnel/mi6_personnel.json';
import qBranch from '@seed/canonical/personnel/q_branch.json';
import bondNpc from '@seed/canonical/personnel/bond_npc.json';
import pcPregens from '@seed/canonical/personnel/pc_pregens.json';
import tarot from '@seed/canonical/personnel/tarot.json';

interface BiographicalBlock {
  summary?: string;
  background?: string;
  narrative_verbatim?: string;
}

interface RawPersonnelEntry {
  id?: string;
  name?: string;
  code_name?: string;
  role?: string;
  allegiance?: string;
  page?: number;
  background_summary?: string;
  biographical?: BiographicalBlock;
  stats?: Record<string, number>;
  skills?: Record<string, { skill_level: number; primary_chance: number }>;
  physical?: Record<string, string | number>;
  combat?: Record<string, string | number>;
}

const PAMELA_PAGE_TRUNC = 200;

function pickSketch(entry: RawPersonnelEntry): string {
  if (typeof entry.background_summary === 'string' && entry.background_summary.length > 0) {
    return entry.background_summary;
  }
  if (entry.biographical && typeof entry.biographical === 'object') {
    const bio = entry.biographical;
    for (const key of ['summary', 'background', 'narrative_verbatim'] as const) {
      const v = bio[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  return entry.role ?? '';
}

function trimToNpcSheet(
  entry: RawPersonnelEntry,
  defaultAllegiance: string | undefined,
): NpcSheetData | null {
  if (typeof entry.id !== 'string' || entry.id.length === 0) return null;
  const name =
    (typeof entry.name === 'string' && entry.name.length > 0 && entry.name) ||
    (typeof entry.code_name === 'string' && entry.code_name.length > 0 && entry.code_name) ||
    entry.id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const role =
    (typeof entry.role === 'string' && entry.role.length > 0 && entry.role) ||
    entry.allegiance ||
    defaultAllegiance ||
    '';
  let sketch = pickSketch(entry);
  if (sketch.length > PAMELA_PAGE_TRUNC) {
    sketch = sketch.slice(0, PAMELA_PAGE_TRUNC - 1) + '…';
  }
  return {
    id: entry.id,
    name,
    role,
    sketch,
    stats: entry.stats ?? {},
    skills: entry.skills ?? {},
    physical: entry.physical ?? {},
    combat: entry.combat ?? {},
    isMook: false,
    page: entry.page,
    allegiance: entry.allegiance ?? defaultAllegiance,
  };
}

function* iterRawEntries(): Generator<{ entry: RawPersonnelEntry; defaultAllegiance?: string }> {
  // Cast via `unknown` because the JSON imports have rich literal shapes
  // that TypeScript can't structurally narrow to RawPersonnelEntry. The
  // runtime guard (typeof entry.id === 'string') below enforces what we
  // actually read.
  const ae = alliesEnemies as unknown as {
    allies?: RawPersonnelEntry[];
    enemies?: RawPersonnelEntry[];
    allies_cross_referenced?: RawPersonnelEntry[];
  };
  for (const e of ae.allies ?? []) yield { entry: e, defaultAllegiance: 'ally' };
  for (const e of ae.enemies ?? []) yield { entry: e, defaultAllegiance: 'enemy' };
  for (const e of ae.allies_cross_referenced ?? []) yield { entry: e, defaultAllegiance: 'ally' };
  const mi6 = mi6Personnel as unknown as { dossiers?: RawPersonnelEntry[] };
  for (const e of mi6.dossiers ?? []) yield { entry: e, defaultAllegiance: 'ally' };
  const q = qBranch as unknown as { dossiers?: RawPersonnelEntry[] };
  for (const e of q.dossiers ?? []) yield { entry: e, defaultAllegiance: 'ally' };
  const pp = pcPregens as unknown as { pregens?: RawPersonnelEntry[] };
  for (const e of pp.pregens ?? []) yield { entry: e, defaultAllegiance: 'pregen' };
  const tar = tarot as unknown as { dossiers?: RawPersonnelEntry[] };
  // A8r Slice B (2026-06-04): tag tarot dossiers with allegiance='tarot' so
  // the ScenarioRunner `isTarot: n.allegiance === 'tarot'` heuristic actually
  // works. Pre-Slice B these NPCs were tagged 'enemy' which made the
  // heuristic always false, leaving the DossierExporter TAROT redaction
  // dead. tarot.json dossiers have no explicit `allegiance` field, so the
  // defaultAllegiance becomes the effective allegiance per trimToNpcSheet.
  for (const e of tar.dossiers ?? []) yield { entry: e, defaultAllegiance: 'tarot' };
}

function buildBondEntry(): NpcSheetData | null {
  const bond = bondNpc as Record<string, unknown>;
  const bio = bond.biographical_background as Record<string, unknown> | undefined;
  const summary =
    (typeof bio?.narrative_verbatim === 'string' && bio.narrative_verbatim) ||
    'James Bond — senior 00 Section operative, code 007.';
  return {
    id: 'james_bond',
    name: 'James Bond',
    role: 'Senior 00 Section operative, code 007',
    sketch: summary.length > PAMELA_PAGE_TRUNC ? summary.slice(0, PAMELA_PAGE_TRUNC - 1) + '…' : summary,
    stats: {},
    skills: {},
    physical: {},
    combat: {},
    isMook: false,
    page: typeof bio?.page === 'number' ? bio.page : undefined,
    allegiance: 'ally',
  };
}

/**
 * Build the personnel catalog once at module load time. The Map is the
 * authoritative source for id → NpcSheetData lookups from useNpcLookup
 * when the id is not in scenario.key_npcs[].
 */
function buildCatalog(): Map<string, NpcSheetData> {
  const out = new Map<string, NpcSheetData>();
  // First-write wins: allies_enemies + mi6 + q_branch are the canonical
  // sources (yielded first via iterRawEntries). pc_pregens follow last
  // and only fill in ids that weren't covered by canonical entries.
  for (const { entry, defaultAllegiance } of iterRawEntries()) {
    const trimmed = trimToNpcSheet(entry, defaultAllegiance);
    if (trimmed && !out.has(trimmed.id)) out.set(trimmed.id, trimmed);
  }
  // bond_npc.json is the canonical source for James Bond as an NPC; this
  // ALWAYS overrides the pc_pregens version (which is a player-character
  // sheet for the same identity, not an NPC profile).
  const bond = buildBondEntry();
  if (bond) out.set(bond.id, bond);
  return out;
}

const PERSONNEL_CATALOG: Map<string, NpcSheetData> = buildCatalog();

export function lookupPersonnel(npcId: string): NpcSheetData | null {
  if (typeof npcId !== 'string' || npcId.length === 0) return null;
  return PERSONNEL_CATALOG.get(npcId) ?? null;
}

export function allPersonnelIds(): string[] {
  return Array.from(PERSONNEL_CATALOG.keys());
}

export function personnelCount(): number {
  return PERSONNEL_CATALOG.size;
}
